import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium';
import { useMemo } from 'react';
import { usePurchaseAgreementsTFunction } from '@retail/purchase-agreements/i18n';
import { AddButton, NoTableRowsOverlay, StackedTextCell } from '@retail/components';
import { Box, Link, Stack, Typography } from '@mui/material';
import { ConditionsNew } from '@retail/calculus/types';
import { getDataGridLocalization, getLLDate, MgPrisLocale } from '@retail/utils';
import { ConditionsTableActions, LevelChip } from '@retail/calculus/components';
import { useRowColorGrouping } from '@retail/hooks';
import { AgreementBinding } from '@retail/purchase-agreements/types';
import { getClassCodeLabel } from '@retail/products/utils';

interface Props {
  conditions: ConditionsNew[];
  language: string;
  loading: boolean;
  conditionActions?: {
    setEdit: (agreement?: ConditionsNew) => void;
    setDeactivateId: (agreement?: number) => void;
    setDeleteId: (conditionId?: number) => void;
  };

  setPriceComparisonBinding: (binding?: AgreementBinding) => void;
  openCreateDialog?: () => void;
}

const baseColumnProps: Partial<GridColDef> = {
  width: 140,
  sortable: false,
  headerAlign: 'left',
  align: 'left',
};

export function PurchaseConditionTable({
  conditions,
  language,
  loading,
  conditionActions,
  setPriceComparisonBinding,
  openCreateDialog,
}: Props) {
  const t = usePurchaseAgreementsTFunction();
  const { getRowColorForStripedTable } = useRowColorGrouping();

  const columns: GridColDef<ConditionsNew>[] = useMemo(
    () => [
      {
        ...baseColumnProps,
        field: 'level',
        headerName: t('purchaseAgreements.agreement.columns.type'),
        width: 190,
        renderCell: ({ row }) => {
          return <LevelChip bindingType={row.bindingType} active={!row.inDraft} />;
        },
      },
      {
        ...baseColumnProps,
        field: 'name',
        headerName: t('purchaseAgreements.agreement.columns.levelOrItemName'),
        width: 260,
        renderCell: ({ row }) => (
          <StackedTextCell topLine={row.bindingTargetName} bottomLine={row.bindingTargetId} />
        ),
      },
      {
        ...baseColumnProps,
        field: 'agreement',
        headerName: t('purchaseAgreements.agreement.columns.agreement'),
        width: 180,
        renderCell: ({ row }) => (
          <Stack display="flex" flexDirection="column">
            <Typography variant="subtitle2" color="text.primary">
              {row.purchaseCondition?.agreementText ?? row.purchaseCondition?.agreementNumber}
            </Typography>
            {row.bindingType === 'MG_SUPPLIER_ITEM' && row.purchaseCondition?.packageType && (
              <Typography variant="body3">
                {getClassCodeLabel(row.purchaseCondition?.packageType)}
              </Typography>
            )}
          </Stack>
        ),
      },
      {
        ...baseColumnProps,
        field: 'activeFromTo',
        headerName: t('purchaseAgreements.agreement.columns.activeFromTo'),
        renderCell: ({ row }) => (
          <StackedTextCell
            topLine={getLLDate(row.purchaseCondition?.validFrom)}
            bottomLine={getLLDate(row.purchaseCondition?.validTo)}
          />
        ),
      },
      {
        ...baseColumnProps,
        field: 'totalItems',
        headerName: t('purchaseAgreements.agreement.columns.priceList'),
        renderCell: ({ row }) => (
          <Link
            component="button"
            disabled={!row.supplier}
            onClick={() =>
              setPriceComparisonBinding({
                ...row.getBindingLevel(),
                agreementId: row.purchaseCondition?.agreementId,
                supplierNumber: row.supplier?.supplierNumber,
              })
            }
            fontWeight={600}
          >
            {t('purchaseAgreements.priceList.lookUpPrices')}
          </Link>
        ),
      },
      {
        ...baseColumnProps,
        field: 'actions',
        headerName: '',
        width: 90,
        disableColumnMenu: true,
        renderCell: ({ row }) => {
          if (!conditionActions) {
            return null;
          }
          const { setDeactivateId, setEdit, setDeleteId } = conditionActions;
          return (
            <ConditionsTableActions
              handleDeactivate={
                !row.inDraft ? () => setDeactivateId(row.purchaseCondition?.conditionId) : undefined
              }
              handleEdit={() => setEdit(row)}
              handleDelete={
                row.inDraft ? () => setDeleteId(row.purchaseCondition?.conditionId) : undefined
              }
            />
          );
        },
      },
    ],
    [t, setPriceComparisonBinding, conditionActions]
  );

  return (
    <Box sx={{ flexGrow: 1, position: 'relative' }}>
      <Box sx={{ position: 'absolute', inset: 0 }}>
        <DataGridPremium
          columns={columns}
          rows={conditions}
          getRowId={(row) => row.purchaseCondition!.conditionId}
          getRowClassName={getRowColorForStripedTable}
          rowHeight={70}
          loading={loading}
          disableRowSelectionOnClick
          disableColumnResize
          disableColumnReorder
          disableColumnMenu
          hideFooter
          localeText={getDataGridLocalization(language as MgPrisLocale)}
          initialState={{ pinnedColumns: { right: ['actions'] } }}
          slots={{
            noRowsOverlay: () => (
              <NoTableRowsOverlay
                noRowsText={t('purchaseAgreements.agreement.noAgreements')}
                addAction={
                  <AddButton onClick={openCreateDialog}>
                    {t('purchaseAgreements.agreement.newAgreement')}
                  </AddButton>
                }
              />
            ),
          }}
        />
      </Box>
    </Box>
  );
}
